import { getBase58Encoder } from '@solana/kit';
import { isAddress } from 'viem';
import { z } from 'zod';

import { ADVANCED_ORDER_TYPES } from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { isEthereumAddress, isSolanaAddress } from '@/utils/models';

const definitiveEvmChainSchema = z.enum([
  'arbitrum',
  'avalanche',
  'base',
  'bsc',
  'ethereum',
  'hyperevm',
  'monad',
  'optimism',
  'plasma',
  'polygon',
  'robinhood',
]);
const definitiveOrderSideSchema = z.enum(['buy', 'sell']);
const definitiveOrderStatusSchema = z.enum([
  'ORDER_STATUS_UNSPECIFIED',
  'ORDER_STATUS_PENDING',
  'ORDER_STATUS_ACCEPTED',
  'ORDER_STATUS_PARTIALLY_FILLED',
  'ORDER_STATUS_FILLED',
  'ORDER_STATUS_CANCELLED',
  'ORDER_STATUS_REJECTED',
  'ORDER_STATUS_TERMINATED',
]);
const definitiveChainStatusSchema = z.enum([
  'CHAIN_STATUS_UNSPECIFIED',
  'CHAIN_STATUS_PROCESSED',
  'CHAIN_STATUS_REORGED',
  'CHAIN_STATUS_FINALIZED',
]);
const definitiveEvmAddressSchema = z
  .string()
  .refine((value: string): boolean => isEthereumAddress(value), 'Invalid EVM address');
const definitiveSvmAddressSchema = z.string().refine(isSolanaAddress, 'Invalid Solana address');

export const definitiveChainByChainId = {
  [CHAIN_IDS.MAINNET]: 'ethereum',
  [CHAIN_IDS.BASE]: 'base',
  [CHAIN_IDS.ROBINHOOD]: 'robinhood',
  [CHAIN_IDS.BSC]: 'bsc',
  [CHAIN_IDS.ARBITRUM]: 'arbitrum',
  [CHAIN_IDS.AVALANCHE]: 'avalanche',
  [CHAIN_IDS.HYPEREVM]: 'hyperevm',
  [CHAIN_IDS.MONAD]: 'monad',
  [CHAIN_IDS.OPTIMISM]: 'optimism',
  [CHAIN_IDS.PLASMA]: 'plasma',
  [CHAIN_IDS.MATIC]: 'polygon',
  [CHAIN_IDS.SOLANA]: 'solana',
} as const satisfies Record<number, DefinitiveChain>;

const definitiveSharedOrderFields = {
  targetAsset: z.string().min(1),
  contraAsset: z.string().min(1),
  qty: z.string().min(1),
  funderAddress: z.string().min(1),
  flashIntegratorFeeBps: z.string().min(1),
  expireTime: z.string().datetime(),
} as const;

const definitiveLimitOrderFields = {
  ...definitiveSharedOrderFields,
  side: definitiveOrderSideSchema,
  orderType: z.literal('limit'),
  limitCrossPrice: z.string().min(1),
} as const;

const definitiveTriggerTypeSchema = z.enum(['lower', 'upper']);
const definitiveTriggerOrderFields = {
  ...definitiveSharedOrderFields,
  side: z.literal('sell'),
  orderType: z.enum(['stop-loss', 'take-profit']),
  triggers: z.tuple([
    z.object({
      crossPrice: z.string().min(1),
      triggerType: definitiveTriggerTypeSchema,
    }),
  ]),
} as const;

const definitiveEvmChainFields = {
  targetAsset: definitiveEvmAddressSchema,
  contraAsset: definitiveEvmAddressSchema,
  funderAddress: definitiveEvmAddressSchema,
  targetChain: definitiveEvmChainSchema,
  contraChain: definitiveEvmChainSchema,
} as const;

const definitiveSvmChainFields = {
  targetAsset: definitiveSvmAddressSchema,
  contraAsset: definitiveSvmAddressSchema,
  funderAddress: definitiveSvmAddressSchema,
  targetChain: z.literal('solana'),
  contraChain: z.literal('solana'),
  svmUseNativeSOL: z.literal(true).optional(),
} as const;

const sameChainRefine = {
  message: 'Definitive orders must use one chain',
} as const;

export const definitiveEvmQuoteRequestSchema = z.union([
  z
    .object({
      ...definitiveLimitOrderFields,
      ...definitiveEvmChainFields,
    })
    .refine((request) => request.targetChain === request.contraChain, sameChainRefine),
  z
    .object({
      ...definitiveTriggerOrderFields,
      ...definitiveEvmChainFields,
    })
    .refine((request) => request.targetChain === request.contraChain, sameChainRefine),
]);

export const definitiveSvmQuoteRequestSchema = z.union([
  z.object({
    ...definitiveLimitOrderFields,
    ...definitiveSvmChainFields,
  }),
  z.object({
    ...definitiveTriggerOrderFields,
    ...definitiveSvmChainFields,
  }),
]);

export const definitiveQuoteRequestSchema = z.union([
  definitiveEvmQuoteRequestSchema,
  definitiveSvmQuoteRequestSchema,
]);

const definitiveSubmitFields = {
  quoteId: z.string().min(1),
  userSignature: z.string().min(1),
} as const;

export const definitiveEvmSubmitOrderRequestSchema = z.union([
  z.object({
    ...definitiveLimitOrderFields,
    ...definitiveEvmChainFields,
    ...definitiveSubmitFields,
    evmOrderTypedData: z.string().min(1),
    evmPermitTypedData: z.string().min(1).optional(),
    evmPermitSignature: z.string().min(1).optional(),
  }),
  z.object({
    ...definitiveTriggerOrderFields,
    ...definitiveEvmChainFields,
    ...definitiveSubmitFields,
    evmOrderTypedData: z.string().min(1),
    evmPermitTypedData: z.string().min(1).optional(),
    evmPermitSignature: z.string().min(1).optional(),
  }),
]);

export const definitiveSvmSubmitOrderRequestSchema = z.union([
  z.object({
    ...definitiveLimitOrderFields,
    ...definitiveSvmChainFields,
    ...definitiveSubmitFields,
    svmNonce: z.string().regex(/^\d+$/),
    svmDeadline: z.string().regex(/^\d+$/),
    svmSponsoredDelegateTx: z.string().min(1).optional(),
  }),
  z.object({
    ...definitiveTriggerOrderFields,
    ...definitiveSvmChainFields,
    ...definitiveSubmitFields,
    svmNonce: z.string().regex(/^\d+$/),
    svmDeadline: z.string().regex(/^\d+$/),
    svmSponsoredDelegateTx: z.string().min(1).optional(),
  }),
]);

export const definitiveSubmitOrderRequestSchema = z.union([
  definitiveEvmSubmitOrderRequestSchema,
  definitiveSvmSubmitOrderRequestSchema,
]);

const definitiveTransactionSchema = z.object({
  to: z.string().min(1),
  data: z.string().min(1),
});

export const definitiveSvmInstructionSchema = z.object({
  programId: z.string().refine(isSolanaAddress),
  accounts: z.array(
    z.object({
      pubkey: z.string().refine(isSolanaAddress),
      isSigner: z.boolean(),
      isWritable: z.boolean(),
    }),
  ),
  data: z.string().refine((value) => {
    try {
      getBase58Encoder().encode(value);
      return true;
    } catch {
      return false;
    }
  }, 'Invalid base58 instruction data'),
});

const definitiveQuoteLegSchema = z.object({
  asset: z.enum(['target', 'contra']),
  amount: z.string(),
  notional: z.string(),
});

export const eip712FieldSchema = z.object({
  name: z.string(),
  type: z.string(),
});
const eip712ChainIdSchema = z
  .union([z.number().int().safe(), z.string().regex(/^(?:0x[0-9a-fA-F]+|[0-9]+)$/)])
  .transform((value) => BigInt(value));
const eip712AddressSchema = z.string().refine(isAddress);
export const eip712TypedDataSchema = z.object({
  domain: z.object({
    name: z.string().optional(),
    version: z.string().optional(),
    chainId: eip712ChainIdSchema,
    verifyingContract: eip712AddressSchema,
  }),
  types: z.record(z.array(eip712FieldSchema)),
  primaryType: z.string().min(1),
  message: z.record(z.unknown()),
});
export const uintStringSchema = z
  .string()
  .regex(/^[0-9]+$/)
  .transform(BigInt);
export const permitSingleMessageSchema = z.object({
  details: z.object({
    token: eip712AddressSchema,
    amount: uintStringSchema,
    expiration: uintStringSchema,
    nonce: uintStringSchema,
  }),
  spender: eip712AddressSchema,
  sigDeadline: uintStringSchema,
});

const definitiveQuoteResponseFields = {
  quoteId: z.string().min(1),
  orderType: z.enum(ADVANCED_ORDER_TYPES),
  side: definitiveOrderSideSchema,
  targetAsset: z.string().min(1),
  contraAsset: z.string().min(1),
  from: definitiveQuoteLegSchema,
  to: definitiveQuoteLegSchema,
  fees: z.object({
    estimatedFeeNotional: z.string(),
  }),
  estimatedPriceImpact: z.string().nullable(),
  wrap: z
    .object({
      nativeAsset: z.string().min(1),
      wrappedAsset: z.string().min(1),
      evmTx: definitiveTransactionSchema
        .extend({
          value: z.string(),
        })
        .nullable(),
      svmInstructions: z.array(definitiveSvmInstructionSchema).nullable(),
    })
    .nullable(),
} as const;

const definitiveEvmQuoteActionsSchema = z.object({
  approveTx: definitiveTransactionSchema.nullable(),
  permitTypedData: z
    .string()
    .transform((value) => value || null)
    .nullable(),
  orderTypedData: z.string().nullable(),
});
const definitiveSvmQuoteActionsSchema = z.object({
  ataSetupIxs: z.array(definitiveSvmInstructionSchema).nullable(),
  delegateIx: definitiveSvmInstructionSchema.nullable(),
  sponsoredDelegateTx: z.string().nullable(),
  orderMessage: z.string().nullable(),
  nonce: z.string().nullable(),
  deadline: z.string().nullable(),
});

export const definitiveEvmQuoteResponseSchema = z.object({
  ...definitiveQuoteResponseFields,
  targetAsset: definitiveEvmAddressSchema,
  contraAsset: definitiveEvmAddressSchema,
  evm: definitiveEvmQuoteActionsSchema,
  svm: z.null(),
});
export const definitiveSvmQuoteResponseSchema = z.object({
  ...definitiveQuoteResponseFields,
  targetAsset: definitiveSvmAddressSchema,
  contraAsset: definitiveSvmAddressSchema,
  evm: z.null(),
  svm: definitiveSvmQuoteActionsSchema,
});
export const definitiveQuoteResponseSchema = z.union([
  definitiveEvmQuoteResponseSchema,
  definitiveSvmQuoteResponseSchema,
]);

export const definitiveSubmitOrderResponseSchema = z.object({
  orderId: z.string().min(1),
});

const definitiveAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  ticker: z.string(),
  chain: z.object({
    id: z.string(),
    name: z.string(),
    namespace: z.string(),
  }),
});
const definitiveDecimalStringSchema = z.string().regex(/^\d+(?:\.\d+)?$/);
const definitiveOrderFilledSchema = z
  .object({
    targetAmount: z.string().nullable(),
    contraAmount: definitiveDecimalStringSchema.nullable(),
    averagePrice: z.string().nullable(),
    averageNotionalPrice: z.string().nullable(),
  })
  .nullable();

export const definitiveOrderSchema = z.object({
  orderId: z.string().min(1),
  orderType: z.enum(['market', 'limit', 'twap', 'stop', 'stop-loss', 'take-profit', 'bracket']),
  side: definitiveOrderSideSchema,
  status: definitiveOrderStatusSchema,
  closeReason: z.string().nullable(),
  funderAddress: z.string(),
  targetAsset: definitiveAssetSchema,
  contraAsset: definitiveAssetSchema,
  qty: definitiveDecimalStringSchema,
  filled: definitiveOrderFilledSchema,
  limitNotionalPrice: z.string().nullable(),
  limitCrossPrice: z.string().nullable(),
  trigger: z.unknown().nullable(),
  brackets: z.array(z.unknown()).nullable(),
  maxPriceImpact: z.string().nullable(),
  twapBucketCount: z.number().int().nullable(),
  placedAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
});

export const definitiveListOrdersResponseSchema = z.object({
  orders: z.array(definitiveOrderSchema),
  nextPageToken: z.string().nullable(),
});

const definitiveFillSchema = z.object({
  status: definitiveChainStatusSchema,
  notional: z.string(),
  venues: z.array(z.string()),
  filledAt: z.string().datetime(),
  rootOrderId: z.string(),
  orderId: z.string(),
  parentOrderId: z.string(),
  transactionId: z.string(),
  fillPrice: z.string(),
  feeAmount: z.string(),
  feeTicker: z.string(),
  tradeFeeAmount: z.string(),
  networkFeeAmount: z.string(),
  integratorFeeAmount: z.string(),
  feeNotional: z.string(),
  tradeFeeNotional: z.string(),
  networkFeeNotional: z.string(),
  integratorFeeNotional: z.string(),
  contraAmount: z.string(),
  targetAmount: z.string(),
});

export const definitiveGetOrderResponseSchema = z.object({
  order: definitiveOrderSchema,
  fills: z.array(definitiveFillSchema),
});
export const definitiveCancelOrderResponseSchema = z.object({
  ok: z.literal(true),
});
export const definitiveErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().nullable().optional(),
  }),
});

export type DefinitiveChain = z.infer<typeof definitiveEvmChainSchema> | 'solana';
export type DefinitiveOrderStatus = z.infer<typeof definitiveOrderStatusSchema>;
export type DefinitiveEvmQuoteRequest = z.infer<typeof definitiveEvmQuoteRequestSchema>;
export type DefinitiveSvmQuoteRequest = z.infer<typeof definitiveSvmQuoteRequestSchema>;
export type DefinitiveQuoteRequest = z.infer<typeof definitiveQuoteRequestSchema>;
export type DefinitiveSubmitOrderRequest = z.infer<typeof definitiveSubmitOrderRequestSchema>;
export type DefinitiveQuoteResponse = z.infer<typeof definitiveQuoteResponseSchema>;
export type DefinitiveEvmQuoteResponse = z.infer<typeof definitiveEvmQuoteResponseSchema>;
export type DefinitiveSvmQuoteResponse = z.infer<typeof definitiveSvmQuoteResponseSchema>;
export type DefinitiveSvmInstruction = z.infer<typeof definitiveSvmInstructionSchema>;
export type DefinitiveSubmitOrderResponse = z.infer<typeof definitiveSubmitOrderResponseSchema>;
export type DefinitiveOrder = z.infer<typeof definitiveOrderSchema>;
export type DefinitiveOrdersPage = z.infer<typeof definitiveListOrdersResponseSchema>;
export type DefinitiveOrderDetail = z.infer<typeof definitiveGetOrderResponseSchema>;
